/* eslint-disable react/no-danger */
import lod_ from "lodash";
import MDBox from "components/Basics/MDBox";
import MDTypography from "components/Basics/MDTypography";
import MDAvatar from "components/Basics/MDAvatar";
import { Chip, Collapse, Divider, Icon, IconButton } from "@mui/material";
import { t } from "i18next";
import { useState } from "react";

/**
 * Extracts display name from an email address (part before @)
 */
const getEmailDisplayName = (email = "") => {
	if (lod_.isNil(email) || lod_.isEmpty(email)) return "";
	if (email.includes("<")) {
		return email.split("<")[0].trim();
	}
	return email.split("@")[0] || email;
};

const getEmailAddress = (email = "") => {
	if (lod_.isNil(email) || lod_.isEmpty(email)) return "";
	if (email.includes("<")) {
		return email.match(/<([^>]+)>/)?.[1] || email;
	}
	return email;
};

/**
 * Filters out empty/nil emails from an array or single value
 */
const getValidEmails = (emails = []) => {
	if (lod_.isNil(emails) || lod_.isEmpty(emails)) return [];
	const list = Array.isArray(emails) ? emails : [emails];
	return list.filter(e => !lod_.isNil(e) && !lod_.isEmpty(e));
};

/**
 * Renders a row of email recipients with a label
 */
const EmailRecipientRow = ({ label, emails = [] }) => {
	const validEmails = getValidEmails(emails);
	if (validEmails.length === 0) return null;

	return (
		<MDBox display="flex" alignItems="start" mb={0.5}>
			<MDTypography
				variant="caption"
				fontWeight="bold"
				sx={{
					color: "text.secondary",
					minWidth: 60,
					mt: 0.5
				}}
			>
				{label}
			</MDTypography>
			<MDBox display="flex" flexWrap="wrap" alignItems="center" flex={1}>
				{validEmails.map((email, index) => (
					<Chip
						key={index}
						label={email}
						size="small"
						sx={{
							mr: 0.5,
							mb: 0.5,
							fontSize: "0.75rem",
							height: 24,
							backgroundColor: "rgba(0, 0, 0, 0.05)",
							"& .MuiChip-label": { px: 1 }
						}}
					/>
				))}
			</MDBox>
		</MDBox>
	);
};

const RenderMailDisplay = ({ mail = {} }) => {
	const [showDetails, setShowDetails] = useState(false);

	if (lod_.isNil(mail) || lod_.isEmpty(mail)) {
		return (
			<MDBox
				display="flex"
				justifyContent="center"
				alignItems="center"
				flexDirection="column"
				sx={{ width: "100%", height: "100%", p: 4 }}
			>
				<Icon sx={{ fontSize: "4rem !important", color: "text.secondary", opacity: 0.5, mb: 2 }}>
					mark_email_read
				</Icon>
				<MDTypography variant="h6" color="text" fontWeight="medium">
					{t("GENERAL.noDataAvailable")}
				</MDTypography>
			</MDBox>
		);
	}

	const fromEmail = Array.isArray(mail.from) ? mail.from[0] : mail.from;
	const toEmails = getValidEmails(mail.to);
	const ccEmails = getValidEmails(mail.cc);
	const bccEmails = getValidEmails(mail.bcc);
	const hasExtraRecipients = ccEmails.length > 0 || bccEmails.length > 0;

	return (
		<MDBox
			sx={{
				width: "100%",
				height: "100%",
				overflowY: "auto",
				display: "flex",
				flexDirection: "column",
				borderRight: "2px solid #e0e0e0"
			}}
			bgColor="white"
		>
			{/* Header Section */}
			<MDBox p={3} pb={2}>
				{/* Subject */}
				<MDTypography variant="h5" fontWeight="bold" sx={{ mb: 3, lineHeight: 1.3 }}>
					{mail.subject || t("GENERAL.noSubject")}
				</MDTypography>

				{/* Sender Info */}
				<MDBox display="flex" alignItems="flex-start">
					<MDAvatar
						variant="gradient"
						bgColor="light"
						size="md"
						sx={{
							mr: 2,
							fontSize: "1rem",
							fontWeight: "bold"
						}}
					></MDAvatar>

					<MDBox flex="1" minWidth={0}>
						<MDBox display="flex" flexDirection="column">
							<MDBox display="flex" alignItems="baseline">
								<MDTypography variant="h6" fontWeight="bold" mr={1}>
									{getEmailDisplayName(fromEmail)}
								</MDTypography>
								<MDTypography
									variant="caption"
									sx={{ color: "text.secondary", fontSize: "0.80rem" }}
								>
									&lt;{getEmailAddress(fromEmail)}&gt;
								</MDTypography>
							</MDBox>

							<MDBox display="flex" alignItems="center" mt={0.5}>
								<MDTypography
									variant="caption"
									sx={{ color: "text.secondary", mr: 1, minWidth: 20 }}
								>
									{t("GENERAL.to")}:
								</MDTypography>
								<MDTypography
									variant="button"
									textTransform="none"
									fontWeight="regular"
									color="text"
								>
									{toEmails.join(", ")}
								</MDTypography>

								{hasExtraRecipients && (
									<IconButton
										size="small"
										onClick={() => setShowDetails(!showDetails)}
										sx={{ ml: 1, p: 0.5, width: 24, height: 24 }}
									>
										<Icon fontSize="small">{showDetails ? "expand_less" : "expand_more"}</Icon>
									</IconButton>
								)}
							</MDBox>
						</MDBox>

						{/* Collapsible Details */}
						<Collapse in={showDetails}>
							<MDBox mt={1} pt={1} borderTop="1px dashed #e0e0e0">
								<EmailRecipientRow label="Cc" emails={ccEmails} />
								<EmailRecipientRow label="Bcc" emails={bccEmails} />
							</MDBox>
						</Collapse>
					</MDBox>
				</MDBox>
			</MDBox>

			<Divider />

			{/* Content Section */}
			<MDBox p={4} mb={2}>
				{mail.content ? (
					<MDBox
						sx={{
							fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
							fontSize: "0.95rem",
							color: "#344767",
							lineHeight: 1.6,
							"& p": { mb: 2 },
							"& a": { color: "#1A73E8", textDecoration: "none" },
							"& a:hover": { textDecoration: "underline" },
							"& blockquote": {
								borderLeft: "3px solid #e0e0e0",
								pl: 2,
								ml: 0,
								color: "text.secondary"
							},
							"& img": { maxWidth: "100%", height: "auto", borderRadius: "8px" }
						}}
					>
						<div dangerouslySetInnerHTML={{ __html: mail.content }} />
					</MDBox>
				) : (
					<MDBox display="flex" justifyContent="center" py={5}>
						<MDTypography variant="body2" color="text" fontStyle="italic">
							{t("GENERAL.noContent")}
						</MDTypography>
					</MDBox>
				)}
			</MDBox>
		</MDBox>
	);
};

export default RenderMailDisplay;
